import { ComputedRef, ref, Ref } from 'vue'

import { ProblemDescription } from '@/domain/entities/problem/problemDescription'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { ProblemDescriptionViewModel } from '@/presentation/viewmodels/problem/problemDescriptionViewModel'
import { waitMathJax } from '@/util/mathJax/waitMathJax'

export class ProblemDescriptionPresenter {
  private viewModel: ProblemDescriptionViewModel
  private problemDomElement: Ref<HTMLDivElement | undefined> = ref()
  private problemHtml: Ref<string> = ref('')

  constructor(viewModel: ProblemDescriptionViewModel) {
    this.viewModel = viewModel
  }

  get problemDescription(): ComputedRef<ProblemDescription> {
    return this.viewModel.problemDescription
  }

  get isLoading(): ComputedRef<boolean> {
    return this.viewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.viewModel.error
  }

  get displayedProblemHtml(): Ref<string> {
    return this.problemHtml
  }

  setProblemDomElement(element: HTMLDivElement | undefined) {
    this.problemDomElement.value = element
  }

  updateProblemHtml() {
    this.problemHtml.value = this.problemDescription.value.html
    this.waitVarMath()
    waitMathJax()
  }

  async getProblemDescription(displayLanguage: DisplayLanguageType, problemId: string): Promise<void> {
    await this.viewModel.getProblemDescription(displayLanguage, problemId)
  }

  private waitVarMath() {
    setTimeout(() => {
      const text = this.problemHtml.value
      if (text) {
        this.problemHtml.value = this.replaceVarWithDollar(this.addDoubleBackslash(text))
      }
    }, 5)
  }

  private replaceVarWithDollar = (input: string) => {
    return input.replace(/<var>(.*?)<\/var>/gs, (match, p1) => {
      return '<var class="set">' + this.setVarmath(p1) + '</var>'
    })
  }

  private addDoubleBackslash = (input: string) => {
    let result = ''
    for (let i = 0; i < input.length; i++) {
      if (input[i] === '\\' && input[i + 1] === '\\') {
        result += '\\\\\\\\'
        i++
      } else {
        result += input[i]
      }
    }
    return result
  }

  private varmath_state = (stateId: number, k: number) => {
    var old_state = stateId
    stateId = k
    return old_state == k || (old_state == 0 && k != 2) || old_state < 0 || k <= 0
      ? ''
      : '<span style="padding:0.25ex;"></span>'
  }

  private varmath_char = (a: any, c: string): string => {
    if (c == '|') {
      return (
        a.state(1) +
        '<span style="padding:0.3ex;">|</span>' +
        this.varmath_next_char(a) +
        '<span style="padding:0.3ex;"></span>'
      )
    }
    if (c == '⇔') {
      return a.state(2) + '⇔'
    }
    if (('a' <= c.toLowerCase() && c.toLowerCase() <= 'z') || c == "'" || ('Α' <= c && c <= 'ϛ')) {
      if (0 <= a.style) return a.state(1) + '<i>' + c + '</i>'
      return a.state(1) + c
    }
    if ('0' <= c && c <= '9') {
      if (a.style <= 0) return a.state(1) + c
      return a.state(1) + '<i>' + c + '</i>'
    }
    if (c == '_') {
      var current_id = a.state_id
      return (
        a.state(-1) +
        '<sub style="font-size:65%">' +
        this.varmath_next_char(a) +
        '<span style="padding:0.2ex;"></span></sub>' +
        a.state(0) +
        a.state(current_id)
      )
    }
    if (c == '^') {
      current_id = a.state_id
      return (
        a.state(-1) +
        '<sup style="font-size:65%">' +
        this.varmath_next_char(a) +
        '<span style="padding:0.2ex;"></span></sup>' +
        a.state(0) +
        a.state(current_id)
      )
    }
    if (c == '{') return this.varmath(a)
    if (c == '*') return a.state(2) + '<span style="vertical-align:-0.25em;">*</span>'
    if (c == ' ' || c == '\n' || c == '\r') return ''
    if (c == '=' || c == '＝') return a.state(2) + '='
    if (c == '/' || c == '／' || c == '⁄') return a.state(2) + '⁄'
    if (c == '[' || c == '［') return a.state(1) + '[' + a.state(0)
    if (c == ']' || c == '］') return a.state(1) + ']' + a.state(0)
    if (c == '(' || c == '（') return a.state(1) + '(' + a.state(0)
    if (c == ')' || c == '）') return a.state(1) + ')' + a.state(0)
    if (c == '<' || c == '＜') return a.state(2) + '&lt;'
    if (c == '>' || c == '＞') return a.state(2) + '&gt;'
    if (c == '≤') return a.state(2) + '≤'
    if (c == '≥') return a.state(2) + '≥'
    if (c == '≦') return a.state(2) + '≦'
    if (c == '≧') return a.state(2) + '≧'
    if (c == '∈') return a.state(2) + '∈'
    if (c == '∋') return a.state(2) + '∋'
    if (c == '≠') return a.state(2) + '≠'
    if (c == '←') return a.state(2) + '←'
    if (c == '→') return a.state(2) + '→'
    if (c == '…') return a.state(2) + '…'
    if (c == '+') return a.state(1) + a.state(2) + '+'
    if (c == '-' || c == '−') {
      if (a.state_id <= 0) return a.state(0) + '−'
      else if (a.state_id == 1) return a.state(2) + '−'
      else if (a.state_id == 2) return a.state(1) + '−'
    }
    if (c == '±') {
      if (a.state_id <= 0) return a.state(0) + '±'
      else if (a.state_id == 1) return a.state(2) + '±'
      else if (a.state_id == 2) return a.state(1) + '±'
    }
    if (c == '×') return a.state(2) + '×'
    if (c == '÷') return a.state(2) + '÷'
    if (c == '∨') return a.state(2) + '∨'
    if (c == '∧') return a.state(2) + '∧'
    if (c == '√') return a.state(2) + '<span style="letter-spacing:-0.1ex;">√</span>'
    if (c == ',') return ',<span style="padding:0.3ex;"></span>'
    return ''
  }

  private varmath_next_char = (a: any): string => {
    var c = a.str[a.pos++]
    if (c == undefined) return ''
    var result = this.varmath_char(a, c)
    if (result != undefined) return result
    if (a.str.substr(a.pos - 1, 3) == '...') {
      a.pos += 2
      return a.state(1) + '…'
    }
    if (c == '\\') {
      var cc = a.str[a.pos]
      if (cc == ' ') {
        a.pos++
        return a.state(0) + '&nbsp;'
      }
      if (cc == ',') {
        a.pos++
        return a.state(0) + '&thinsp;'
      }
      if (cc == '\\') {
        a.pos++
        return a.state(0) + '<br>'
      }
      let p = 0
      if ('a' <= cc.toLowerCase() && cc.toLowerCase() <= 'z') {
        for (p = a.pos; 'a' <= a.str[p].toLowerCase() && a.str[p].toLowerCase() <= 'z'; ) p++
        var key = a.str.substr(a.pos, p - a.pos)
        a.pos = p
        if (key == 'lt') return this.varmath_char(a, '<')
        if (key == 'gt') return this.varmath_char(a, '>')
        if (key == 'neq') return this.varmath_char(a, '≠')
        if (key == 'leq') return this.varmath_char(a, '≤')
        if (key == 'geq') return this.varmath_char(a, '≥')
        if (key == 'in') return this.varmath_char(a, '∈')
        if (key == 'ni') return this.varmath_char(a, '∋')
        if (key == 'times') return this.varmath_char(a, '×')
        if (key == 'pm') return this.varmath_char(a, '±')
        if (key == 'rightarrow') return this.varmath_char(a, '→')
        if (key == 'leftarrow') return this.varmath_char(a, '←')
        if (key == 'rm') {
          a.style = -1
          return ''
        }
        if (key == 'it') {
          a.style = 1
          return ''
        }
        if (key == 'frac') {
          return (
            '<table style="display:inline-table;vertical-align:middle;border-collapse:collapse;border-spacing:0;margin-bottom:0.8ex;"><tr><td class="problemDom-table" style="text-align:center;font-size:90%;padding:0.3ex;">' +
            a.state(0) +
            this.varmath_next_char(a) +
            '</td></tr><tr><td style="text-align:center;font-size:90%;padding:0.3ex;">' +
            a.state(0) +
            this.varmath_next_char(a) +
            '</td></tr></table>'
          )
        }
        if (key == 'sqrt')
          return (
            this.varmath_char(a, '√') +
            '<span class="problemDom-sqrt">' +
            '<span style="font-size:85%;">' +
            this.varmath_next_char(a) +
            '</span>&thinsp;</span>'
          )
        if (key == 'mathbb')
          return (
            '<span style="' +
            'font-weight:bold;-webkit-text-fill-color:#fff;' +
            '-webkit-text-stroke:1px #000;">' +
            this.varmath_next_char(a).split('<i>').join('').split('</i>').join('') +
            '</span>'
          )
        return '[' + key + ']'
      }
      return a.state(0) + a.str[a.pos++]
    }
    if (a.style <= 0) return a.state(1) + c
    return a.state(1) + '<i>' + c + '</i>'
  }

  private varmath = (a: any): string => {
    if (a.space == undefined) a.space = ''
    if (a.state_id == undefined) a.state_id = -1
    if (a.style == undefined) a.style = 0
    var current_style = a.style
    let result = ''
    for (result = ''; a.str[a.pos] != undefined && a.str[a.pos] != '}'; ) result += this.varmath_next_char(a)
    if (a.str[a.pos] == '}') a.pos++
    a.style = current_style
    return result.split('</i><i>').join('')
  }

  private setVarmath = (input: string): string => {
    const tr: Record<string, string> = {
      '<sub>': '_{',
      '</sub>': '}',
      '<sup>': '^{',
      '</sup>': '}',
      '&lt;': ' \\lt ',
      '&gt;': ' \\gt ',
      '&le;': ' \\leq ',
      '&ge;': ' \\geq ',
      '&ne;': ' \\neq ',
      '&in;': ' \\in ',
      '&ni;': ' \\ni ',
      '&times;': ' \\times ',
      '&pm;': ' \\pm ',
      '&rightarrow;': ' \\rightarrow ',
      '&leftarrow;': ' \\leftarrow ',
      '\\lt': ' < ',
      '\\gt': ' > ',
      '\\leq': ' ≤ ',
      '\\geq': ' ≥ ',
      '\\neq': ' ≠ ',
      '\\in': ' ∈ ',
      '\\ni': ' ∋ ',
      '\\times': ' × ',
      '\\pm': ' ± ',
      '\\rightarrow': ' → ',
      '\\leftarrow': ' ← '
    }

    let html = input

    for (const key in tr) {
      html = html.split(key).join(tr[key])
    }

    const result = this.varmath({ str: html, pos: 0, state: this.varmath_state })

    return (
      '<span class="problemDom-text" style="font-family: \'Times New Roman\', Times, Georgia, serif;font-style:normal;font-size:110%;">' +
      result +
      '</span>'
    )
  }
}
